import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Hero from '../components/ui/Hero';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiHeart, FiSend, FiVideo, FiFileText } = FiIcons;

const Testimonies = () => {
  const [filter, setFilter] = useState('all');

  const testimonies = [
    {
      id: 1,
      type: 'video',
      name: 'Sarah Martinez',
      location: 'University of Florida',
      category: 'campus',
      title: 'From Darkness to Light',
      preview: 'I was struggling with depression and felt completely lost. When Love 180 reached out to me on campus, everything changed...',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      duration: '3:42'
    },
    {
      id: 2,
      type: 'text',
      name: 'Marcus Thompson',
      location: 'Downtown Orlando',
      category: 'city',
      title: 'Addiction to Freedom',
      preview: 'For years, I was trapped in addiction with no hope. During a Love 180 street outreach, someone prayed with me and my life turned 180 degrees...',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      fullStory: 'For years, I was trapped in addiction with no hope. I had lost my job, my family, and nearly my life. During a Love 180 street outreach in downtown Orlando, someone approached me with genuine love and compassion. They didn\'t judge me or try to fix me immediately. Instead, they listened to my story and prayed with me right there on the street. That encounter planted a seed that grew into complete transformation. Today, I\'m 18 months sober, restored with my family, and actively serving in ministry. God truly turned my life 180 degrees around.'
    },
    {
      id: 3,
      type: 'video',
      name: 'Jessica Rodriguez',
      location: 'Florida State University',
      category: 'campus',
      title: 'Finding Purpose',
      preview: 'I had everything I thought I wanted - good grades, popularity, success - but I felt empty inside. Love 180 showed me true purpose...',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      duration: '4:15'
    },
    {
      id: 4,
      type: 'text',
      name: 'David Kim',
      location: 'Miami Beach',
      category: 'city',
      title: 'Healing from Loss',
      preview: 'After losing my brother in an accident, I was angry at God and everyone around me. A Love 180 prayer walk team encountered me...',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      fullStory: 'After losing my brother in a car accident, I was consumed with anger and grief. I blamed God and pushed away everyone who tried to help. During one of my darkest moments, while walking alone on Miami Beach, I encountered a Love 180 prayer team. They didn\'t try to give me easy answers or quick fixes. Instead, they sat with me in my pain and showed me the heart of Jesus. Through their consistent love and the community they connected me to, I began to heal. God didn\'t erase my pain, but He gave it purpose. Now I help others who are walking through loss and grief.'
    },
    {
      id: 5,
      type: 'video',
      name: 'Amanda Foster',
      location: 'University of Central Florida',
      category: 'campus',
      title: 'Breaking Generational Cycles',
      preview: 'Growing up in a broken home, I thought my future was predetermined. Love 180 showed me that God had different plans...',
      image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      duration: '5:28'
    },
    {
      id: 6,
      type: 'text',
      name: 'Carlos Mendez',
      location: 'Downtown Tampa',
      category: 'city',
      title: 'From Gang Life to Ministry',
      preview: 'I was deep in gang life, thinking there was no way out. A Love 180 worship night in the park changed everything...',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      fullStory: 'I was deep in gang life from the age of 14. Violence, drugs, and crime were all I knew. I thought there was no way out - that this was my destiny. One evening, I heard music coming from a park in downtown Tampa. It was a Love 180 worship night. Something about the atmosphere drew me in, even though everything in me said to walk away. The love I felt from the people there was unlike anything I had experienced. They didn\'t see a gang member; they saw a son of God. That night, I gave my life to Jesus and began the long journey out of gang life. It wasn\'t easy, but the Love 180 community walked with me every step of the way. Today, I\'m studying to become a pastor and working with at-risk youth in my community.'
    }
  ];

  const filteredTestimonies = filter === 'all' 
    ? testimonies 
    : testimonies.filter(t => t.category === filter);

  const [selectedTestimony, setSelectedTestimony] = useState(null);

  return (
    <div>
      <Hero
        title="Lives Transformed"
        description="Real stories from real people whose lives have been turned 180 degrees by the power of Jesus Christ. These testimonies represent the heart of our ministry - transformation through authentic encounters with God's love."
        primaryCta={{
          text: 'Share Your Story',
          href: '#submit-testimony'
        }}
        backgroundImage="https://images.unsplash.com/photo-1529390079861-591de354faf5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1950&q=80"
      />

      {/* Filter Navigation */}
      <Section padding="sm" background="gray">
        <div className="flex justify-center">
          <div className="bg-white rounded-lg p-1 shadow-sm">
            <div className="flex space-x-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                All Stories
              </button>
              <button
                onClick={() => setFilter('campus')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === 'campus' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                Campus Stories
              </button>
              <button
                onClick={() => setFilter('city')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  filter === 'city' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-neutral-600 hover:text-primary-600'
                }`}
              >
                City Stories
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Testimonies Grid */}
      <Section>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTestimonies.map((testimony, index) => (
            <motion.div
              key={testimony.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="overflow-hidden h-full flex flex-col">
                <div className="relative">
                  <img
                    src={testimony.image}
                    alt={testimony.name}
                    className="w-full h-48 object-cover"
                  />
                  {testimony.type === 'video' && (
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <div className="bg-white bg-opacity-90 rounded-full p-3">
                        <SafeIcon icon={FiPlay} className="w-6 h-6 text-primary-600" />
                      </div>
                    </div>
                  )}
                  {testimony.duration && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                      {testimony.duration}
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <div className="bg-primary-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                      <SafeIcon icon={testimony.type === 'video' ? FiVideo : FiFileText} className="w-3 h-3" />
                      <span>{testimony.type === 'video' ? 'Video' : 'Story'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">{testimony.title}</h3>
                    <div className="text-sm text-neutral-600 mb-3">
                      <span className="font-medium">{testimony.name}</span> • {testimony.location}
                    </div>
                    <p className="text-neutral-600 mb-4">{testimony.preview}</p>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => setSelectedTestimony(testimony)}
                  >
                    {testimony.type === 'video' ? 'Watch Story' : 'Read Full Story'}
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Submit Testimony Section */}
      <Section id="submit-testimony" background="gray">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
            Share Your 180° Story
          </h2>
          <p className="text-xl text-neutral-600 mb-8">
            Has God transformed your life through Love 180 ministry? We'd love to hear your story and share it to encourage others.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <Card className="p-6">
              <SafeIcon icon={FiVideo} className="w-8 h-8 text-primary-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-3">Video Testimony</h3>
              <p className="text-neutral-600 mb-4">
                Record a video sharing your transformation story. Perfect for social media and website features.
              </p>
              <Button variant="primary" size="sm">Submit Video</Button>
            </Card>
            
            <Card className="p-6">
              <SafeIcon icon={FiFileText} className="w-8 h-8 text-primary-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-3">Written Story</h3>
              <p className="text-neutral-600 mb-4">
                Write out your testimony to be featured on our website and in ministry materials.
              </p>
              <Button variant="primary" size="sm">Submit Story</Button>
            </Card>
          </div>
          
          <div className="bg-white p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Quick Testimony Form</h4>
            <form className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Your Name"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <input
                type="text"
                placeholder="Where did your encounter with Love 180 happen?"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <textarea
                rows={4}
                placeholder="Share your transformation story in a few sentences..."
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <Button type="submit" variant="primary">
                <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
                Submit Testimony
              </Button>
            </form>
          </div>
        </div>
      </Section>

      {/* Modal for expanded testimony */}
      {selectedTestimony && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold">{selectedTestimony.title}</h2>
                  <p className="text-neutral-600">{selectedTestimony.name} • {selectedTestimony.location}</p>
                </div>
                <button
                  onClick={() => setSelectedTestimony(null)}
                  className="text-neutral-400 hover:text-neutral-600"
                >
                  ✕
                </button>
              </div>
              
              <img
                src={selectedTestimony.image}
                alt={selectedTestimony.name}
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              
              {selectedTestimony.type === 'video' ? (
                <div className="bg-neutral-100 p-8 rounded-lg text-center">
                  <SafeIcon icon={FiPlay} className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                  <p className="text-neutral-600">Video player would be embedded here</p>
                  <p className="text-sm text-neutral-500 mt-2">Duration: {selectedTestimony.duration}</p>
                </div>
              ) : (
                <div className="prose max-w-none">
                  <p className="text-neutral-700 leading-relaxed">{selectedTestimony.fullStory}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testimonies;